import React from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const useStyles = makeStyles((theme) => ({
  datePickerWrapper: {
    '& .react-datepicker-popper': {
      zIndex: 9999,
    },
  },
}));

const TableQRCodeEditDialog = ({
  open,
  onClose,
  tableNumber,
  editPaymentOption,
  setEditPaymentOption,
  editDisabled,
  setEditDisabled,
  selectedDate,
  setSelectedDate,
  neverExpire,
  setNeverExpire,
  onUpdate,
  isUpdating,
  formatExpireTime,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const handleSetTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    setSelectedDate(tomorrow);
    setNeverExpire(false);
  };

  const handleSetNeverExpire = () => {
    setNeverExpire(true);
    setSelectedDate(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit QR Code Settings - {tableNumber}</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Update payment settings and expiry date for the QR code.
          </Typography>
        </Box>
        
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Payment Option */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Online Payment Option</FormLabel>
            <RadioGroup 
              value={editPaymentOption} 
              onChange={(e) => setEditPaymentOption(e.target.value)}
            >
              <FormControlLabel value="notRequired" control={<Radio />} label="Not Required" />
              <FormControlLabel value="required" control={<Radio />} label="Required" />
              <FormControlLabel value="optional" control={<Radio />} label="Optional" />
            </RadioGroup>
          </FormControl>

          <Divider />

          {/* QR Code Status */}
          <FormControl component="fieldset">
            <FormLabel component="legend">QR Code Status</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={!editDisabled}
                  onChange={(e) => setEditDisabled(!e.target.checked)}
                  color="primary"
                />
              }
              label={editDisabled ? 'Disabled (QR code is inactive)' : 'Enabled (QR code is active)'}
            />
            <Typography variant="caption" color="textSecondary">
              When disabled, this QR code cannot be used to place orders
            </Typography>
          </FormControl>

          <Divider />

          {/* Expiry Settings */}
          <Typography variant="subtitle2" gutterBottom>
            Expiry Settings
          </Typography>
          
          <Button
            variant={neverExpire ? "contained" : "outlined"}
            color="primary"
            onClick={handleSetNeverExpire}
          >
            Never Expire
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSetTomorrow}
          >
            Set to Tomorrow
          </Button>
          <Box className={classes.datePickerWrapper}>
            <Typography variant="body2" style={{ marginBottom: theme.spacing(1) }}>
              Or pick a custom date:
            </Typography>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setNeverExpire(false);
              }}
              showTimeSelect
              dateFormat="dd/MM/yyyy h:mm aa"
              minDate={new Date()}
              placeholderText="Select expiry date and time"
              popperProps={{ strategy: "fixed" }}
            />
          </Box>
          {neverExpire && (
            <Typography variant="body2" color="primary">
              Selected: Never Expire
            </Typography>
          )}
          {selectedDate && !neverExpire && (
            <Typography variant="body2" color="primary">
              Selected: {formatExpireTime(selectedDate.getTime())}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onUpdate}
          color="primary"
          variant="contained"
          disabled={(!selectedDate && !neverExpire) || isUpdating}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableQRCodeEditDialog;
